import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicHeader from '../../components/ui/PublicHeader';
import Footer from '../homepage/components/Footer';
import { Search, Filter, Star, Home, Wind, Zap } from 'lucide-react';
import { productService } from '../../services/productService';
import Icon from '../../components/AppIcon';


const ProductListing = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [priceRange, setPriceRange] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);

  const categories = [
    { value: 'all', label: 'All Types', icon: Filter },
    { value: 'cordless_stick', label: 'Cordless Stick', icon: Zap },
    { value: 'upright', label: 'Upright', icon: Wind },
    { value: 'robot', label: 'Robot', icon: Home },
    { value: 'canister', label: 'Canister', icon: Wind },
    { value: 'handheld', label: 'Handheld', icon: Zap },
  ];

  const priceRanges = [
    { value: 'all', label: 'All Prices' },
    { value: '0-100', label: 'Under $100' },
    { value: '100-300', label: '$100 - $300' },
    { value: '300-500', label: '$300 - $500' },
    { value: '500+', label: '$500+' },
  ];

  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' },
    { value: 'name', label: 'Name: A to Z' },
  ];

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const data = await productService?.getAllProducts();
      setProducts(data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredAndSortedProducts = () => {
    let filtered = [...products];

    // Search filter
    if (searchQuery) {
      filtered = filtered?.filter(product =>
        product?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        product?.brand?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        product?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered?.filter(product => product?.category === selectedCategory);
    }

    // Brand filter
    if (selectedBrand !== 'all') {
      filtered = filtered?.filter(product => product?.brand === selectedBrand);
    }

    // Price range filter
    if (priceRange !== 'all') {
      const [min, max] = priceRange?.split('-')?.map(v => v === '+' ? Infinity : parseFloat(v));
      filtered = filtered?.filter(product => {
        const price = product?.current_price || 0;
        return max ? (price >= min && price <= max) : price >= min;
      });
    }

    // Sorting
    switch (sortBy) {
      case 'price-low':
        filtered?.sort((a, b) => (a?.current_price || 0) - (b?.current_price || 0));
        break;
      case 'price-high':
        filtered?.sort((a, b) => (b?.current_price || 0) - (a?.current_price || 0));
        break;
      case 'name':
        filtered?.sort((a, b) => (a?.name || '')?.localeCompare(b?.name || ''));
        break;
      case 'newest':
      default:
        filtered?.sort((a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0));
        break;
    }

    return filtered;
  };

  const filteredProducts = getFilteredAndSortedProducts();
  const uniqueBrands = ['all', ...new Set(products.map(p => p?.brand).filter(Boolean))];

  const getBadgeStyles = (badge) => {
    const styles = {
      editors_choice: 'bg-purple-100 text-purple-700',
      best_value: 'bg-green-100 text-green-700',
      premium_choice: 'bg-yellow-100 text-yellow-700',
      top_rated: 'bg-blue-100 text-blue-700',
      new_arrival: 'bg-pink-100 text-pink-700',
    };
    return styles?.[badge] || 'bg-gray-100 text-gray-700';
  };

  const formatBadgeText = (badge) => {
    return badge?.split('_')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' ');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PublicHeader />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-4">Vacuum Cleaner Catalog</h1>
          <p className="text-xl text-indigo-100">Explore our comprehensive collection of expert-reviewed vacuum cleaners</p>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filter Bar */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search by name, brand, or features..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e?.target?.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              {sortOptions?.map(option => (
                <option key={option?.value} value={option?.value}>{option?.label}</option>
              ))}
            </select>

            {/* Filter Toggle (Mobile) */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="md:hidden flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Filter className="h-5 w-5" />
              Filters
            </button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar Filters */}
          <aside className={`md:w-64 space-y-6 ${showFilters ? 'block' : 'hidden md:block'}`}>
            {/* Category Filter */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Category</h3>
              <div className="space-y-2">
                {categories?.map(category => {
                  const Icon = category?.icon;
                  const count = category?.value === 'all' 
                    ? products?.length 
                    : products?.filter(p => p?.category === category?.value)?.length;
                  return (
                    <button
                      key={category?.value}
                      onClick={() => setSelectedCategory(category?.value)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                        selectedCategory === category?.value
                          ? 'bg-indigo-50 text-indigo-600 font-medium' :'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <Icon className="h-4 w-4" />
                        {category?.label}
                      </span>
                      <span className="text-xs text-gray-500">({count})</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Brand Filter */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Brand</h3>
              <select
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                {uniqueBrands?.map(brand => (
                  <option key={brand} value={brand}>
                    {brand === 'all' ? 'All Brands' : brand}
                  </option>
                ))}
              </select>
            </div>

            {/* Price Range Filter */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Price Range</h3>
              <div className="space-y-2">
                {priceRanges?.map(range => (
                  <button
                    key={range?.value}
                    onClick={() => setPriceRange(range?.value)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      priceRange === range?.value
                        ? 'bg-indigo-50 text-indigo-600 font-medium' :'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {range?.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Clear Filters */}
            {(selectedCategory !== 'all' || selectedBrand !== 'all' || priceRange !== 'all' || searchQuery) && (
              <button
                onClick={() => {
                  setSelectedCategory('all');
                  setSelectedBrand('all');
                  setPriceRange('all');
                  setSearchQuery('');
                }}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Clear All Filters
              </button>
            )}
          </aside>

          {/* Product Grid */}
          <main className="flex-1">
            {/* Results Count */}
            <div className="mb-4 text-sm text-gray-600">
              Showing {filteredProducts?.length} of {products?.length} products
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
              </div>
            ) : filteredProducts?.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <p className="text-gray-500 text-lg">No products found matching your criteria.</p>
                <button
                  onClick={() => {
                    setSelectedCategory('all');
                    setSelectedBrand('all');
                    setPriceRange('all');
                    setSearchQuery('');
                  }}
                  className="mt-4 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProducts?.map(product => (
                  <Link
                    key={product?.id}
                    to={`/product-review/${product?.id}`}
                    className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden group"
                  >
                    {/* Product Image */}
                    <div className="relative aspect-square bg-gray-100">
                      <img
                        src={product?.image_url || '/assets/images/no_image.png'}
                        alt={product?.image_alt || product?.name || 'Product image'}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      {product?.badge && (
                        <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold ${getBadgeStyles(product?.badge)}`}>
                          {formatBadgeText(product?.badge)}
                        </div>
                      )}
                      {product?.discount > 0 && (
                        <div className="absolute top-3 right-3 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                          -{product?.discount}%
                        </div>
                      )}
                    </div>

                    {/* Product Info */}
                    <div className="p-4">
                      <div className="text-xs text-gray-500 mb-1">{product?.brand}</div>
                      <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-indigo-600 transition-colors">
                        {product?.name}
                      </h3>
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                        {product?.description}
                      </p>

                      {/* Price */}
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-2xl font-bold text-gray-900">
                          ${product?.current_price?.toFixed(2)}
                        </span>
                        {product?.original_price && product?.original_price > product?.current_price && (
                          <span className="text-sm text-gray-500 line-through">
                            ${product?.original_price?.toFixed(2)}
                          </span>
                        )}
                      </div>

                      {/* CTA */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-indigo-600 font-medium group-hover:underline">
                          View Details →
                        </span>
                        <Star className="h-5 w-5 text-yellow-400 fill-current" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ProductListing;